import Store from '@store';
import { constants } from '@config';
import { getLinkedTextAreaId, getLinkedTextAreaPageId } from 'src/helpers/text-overflow-links';
import { removeFalsyAttributes } from 'src/utils/deep-merge';

const RestrictionsDecorate = require('../../restrictions/decorate');
const TextRestrictions = require('../setters/restrictions/text');

const ElementSetter = require('./abstract/element');

module.exports = TextSetter;

function TextSetter () {
  this.textAttributes = [
    'anchor',
    'auto_adjustable',
    'bullet_points',
    'color',
    'columns_balance',
    'columns_gap',
    'columns',
    'content',
    'data_paragraph_style',
    'data_meta',
    'decoration',
    'font',
    'heading',
    'highlight',
    'ordered_list',
    'overflow_content',
    'spacing',
    'stroke',
    'text_content',
    'text_overflow'
  ];

  this.handledAttributes = _.union(this.textAttributes, this.handledAttributes);
  this.on('all', _.bind(function (event, id, data) {
    if (event === 'smart_asset') return;

    if (event === 'overflow_content' || data?.isTextOverflow) {
      this.linkedTextList.render(this.canvas);
    } else {
      (event || data?.keep_box_size) && this.textarea.render(this.canvas);
    }
  }, this));
}

TextSetter.prototype = new ElementSetter();
TextSetter.prototype.constructor = TextSetter;

TextSetter.prototype.set = function (element, data) {
  this.textarea = data.textarea || new INKIVE.Text.Textarea().setDOM(element, false);
  this.setLinkedTextList(data);
  ElementSetter.prototype.set.apply(this, arguments);
};

TextSetter.prototype.setLinkedTextList = function (data) {
  if (typeof data.text_overflow === 'undefined' && !data.isTextOverflow) return;

  this.linkedTextList = new INKIVE.Text.LinkedTextList();
  let currentTextarea = this.textarea;
  for (let i = 0; i < constants.text.maxTextOverflowLength; i++) {
    this.linkedTextList.addEnd(currentTextarea);
    const childPageId = currentTextarea.child_text
      ? getLinkedTextAreaPageId(currentTextarea.child_text)
      : null;
    const childId = currentTextarea.child_text
      ? getLinkedTextAreaId(currentTextarea.child_text)
      : null;
    const childText = childPageId === Number(INKIVE.Tool._current_design_id)
      ? document.querySelector(`g#${childId}`)
      : document.querySelector(`g#${childId}[data-page-id="${childPageId}"]`);

    if (!childText) break;
    currentTextarea = new INKIVE.Text.Textarea().setDOM(childText, false, true);
  }
};

TextSetter.prototype.updateDataParagraphStyle = function (element, data) {
  const { id } = data.data_paragraph_style;
  const styleData = Store.getState().metadata.paragraph_styles.styles[id];
  const range = this.canvas.runExtensions('selectionRange', {});
  const attributes = removeFalsyAttributes({
    custom_font_id: styleData.custom_font_id,
    font_stretch: styleData.font_stretch,
    data_paragraph_style: id,
    fill: styleData.fill,
    font_family: styleData.font_family,
    font_size: styleData.font_size,
    font_weight: styleData.font_weight,
    font_style: styleData.font_style
  });

  if (range.start === range.end && range.start === 0) {
    this.textarea.text.styles = this.textarea.text.styles.filter(style => {
      return !attributes[style.attribute.replace(/-/g, '_')];
    });

    this.textarea.text.setValues(attributes);
    this.textarea.text.strings.forEach(string => {
      string.setValues(removeFalsyAttributes({
        bulletFill: attributes.fill,
        bulletFontSize: attributes.font_size,
        bulletFontWeight: attributes.font_weight,
        orderedFill: attributes.fill,
        orderedFontSize: attributes.font_size,
        orderedFontWeight: attributes.font_weight
      }));
    });
  } else {
    this.textarea.text.setMultiplePartialStyling(range.start, range.end, removeFalsyAttributes({
      fill: attributes.fill,
      'custom-font-id': attributes.custom_font_id,
      'font-stretch': attributes.font_stretch,
      'font-family': attributes.font_family,
      'font-size': attributes.font_size,
      'font-weight': attributes.font_weight,
      'font-style': attributes.font_style,
      'data-paragraph-style': id
    }));

    const realRangeStart = this.textarea.text.fakeCharIndexToReal(range.start);
    const realRangeEnd = this.textarea.text.fakeCharIndexToReal(range.end);

    const { startLineToStyle, lastLineToStyle } = INKIVE.Helpers.getStylingPart(range.start, range.end, this.textarea.text.dom);
    for (let lineIndex = startLineToStyle; lineIndex <= lastLineToStyle; lineIndex++) {
      const isFirstCharSelected = realRangeStart <= this.textarea.text.strings[lineIndex].startIndex && realRangeEnd >= this.textarea.text.strings[lineIndex].startIndex + 1;
      if (!isFirstCharSelected) continue;

      this.textarea.text.strings[lineIndex].bulletFill = attributes.fill;
      this.textarea.text.strings[lineIndex].bulletFontSize = attributes.font_size;
      this.textarea.text.strings[lineIndex].orderedFill = attributes.fill;
      this.textarea.text.strings[lineIndex].orderedFontSize = attributes.font_size;
      this.textarea.text.strings[lineIndex].orderedFontWeight = attributes.font_weight;
    }
  }

  this.canvas.runExtensions('renderTextArea', { textArea: this.textarea });
};

TextSetter.prototype.updateDataMeta = function (element, data) {
};

TextSetter.prototype.updateStroke = function (element, data) {
  data = data.stroke;

  if (typeof data.width !== 'undefined') {
    this.textarea.text.setValues({ stroke_width: data.width });
    this.trigger('stroke.width', element.getAttribute('id'), data.width);
  }

  if (typeof data.color !== 'undefined') {
    this.textarea.text.setValues({ stroke: data.color });
    this.trigger('stroke.color', element.getAttribute('id'), data.color);
  }

  if (typeof data.dasharray !== 'undefined') {
    this.textarea.text.setValues({ stroke_dasharray: data.dasharray });
    this.trigger('stroke.dasharray', element.getAttribute('id'), data.dasharray);
  }

  this.trigger('stroke', element.getAttribute('id'), data);
};

TextSetter.prototype.updateHighlight = function (element, data) {
  data = data.highlight;

  if (typeof data.color !== 'undefined') {
    this.textarea.text.setValues({ highlight: data.color });
    const textLength = this.textarea.text.textContent.length;
    this.textarea.text.removeIncludedStyles('highlight', 0, textLength);
    this.trigger('highlight.color', element.getAttribute('id'), data.color);
  }

  this.trigger('highlight', element.getAttribute('id'), data);
};

TextSetter.prototype.updateSpacing = function (element, data) {
  const isUnsettingUnderline = typeof data.decoration !== 'undefined' && data.decoration !== 'underline';
  const hasUnderline = this.textarea.text.text_decoration === 'underline' && !isUnsettingUnderline;

  data = data.spacing;

  if (hasUnderline && (data.letter || data.word)) {
    require('../../../utils/advice').show('spacing-and-underline');
  }

  if (typeof data.letter !== 'undefined' && (!hasUnderline || !data.letter)) {
    let letterSpacing = data.letter;

    if (_.isArray(this.textarea.text.letter_spacing)) {
      const oldAvg = _.reduce(this.textarea.text.letter_spacing, function (memo, num) {
        return memo + parseFloat(num);
      }, 0) / (this.textarea.text.letter_spacing.length - 1);

      const rationIncrement = letterSpacing / (oldAvg === 0 ? 1 : oldAvg);

      letterSpacing = _.map(this.textarea.text.letter_spacing, function (dx) {
        return parseFloat(dx) * rationIncrement;
      });
    }

    this.textarea.text.setValues({ letter_spacing: letterSpacing });
    this.trigger('spacing.letter', element.getAttribute('id'), data.letter);
  }

  if (typeof data.word !== 'undefined' && (!hasUnderline || !data.word)) {
    this.textarea.text.setValues({ word_spacing: data.word });
    this.trigger('spacing.word', element.getAttribute('id'), data.word);
  }

  if (typeof data.paragraph !== 'undefined') {
    this.textarea.text.setValues({ paragraph_spacing: data.paragraph });
    this.trigger('spacing.paragraph', element.getAttribute('id'), data.paragraph);
  }

  if (typeof data.first_line_margin !== 'undefined') {
    this.textarea.text.setValues({ first_line_margin: data.first_line_margin });
    this.trigger('spacing.first_line_margin', element.getAttribute('id'), data.first_line_margin);
  }

  if (typeof data.right_margin !== 'undefined') {
    this.textarea.text.setValues({ right_margin: data.right_margin });
    this.trigger('spacing.right_margin', element.getAttribute('id'), data.right_margin);
  }

  if (typeof data.line !== 'undefined') {
    this.textarea.text.setValues({ line_spacing: data.line });
    this.trigger('spacing.line', element.getAttribute('id'), data.line);
  }

  if (typeof data.margin !== 'undefined') {
    this.textarea.text.setValues({ line_margin: data.margin });
    this.trigger('spacing.margin', element.getAttribute('id'), data.margin);
  }

  this.trigger('spacing', element.getAttribute('id'), data);
};

TextSetter.prototype.updateFont = function (element, data) {
  data = data.font;

  if (typeof data.family !== 'undefined') {
    this.textarea.text.setValues({ font_family: data.family });
    this._processFontFamily(element, data);
    this.trigger('font.family', element.getAttribute('id'), data.family);
  }

  if (typeof data.size !== 'undefined') {
    const fontSize = Math.precision(parseFloat(data.size), 1);
    this.textarea.text.setValues({ font_size: fontSize });
    this.trigger('font.size', element.getAttribute('id'), fontSize);
  }

  if (typeof data.weight !== 'undefined') {
    this.textarea.text.setValues({ font_weight: data.weight });
    this.trigger('font.weight', element.getAttribute('id'), data.weight);
  }

  if (typeof data.style !== 'undefined') {
    this.textarea.text.setValues({ font_style: data.style });
    this.trigger('font_style', element.getAttribute('id'), data.style);
  }

  if (typeof data.stretch !== 'undefined') {
    this.textarea.text.setValues({ font_stretch: data.stretch });
    this.trigger('font_stretch', element.getAttribute('id'), data.stretch);
  }

  this.trigger('font', element.getAttribute('id'), data);
};

TextSetter.prototype._processFontFamily = function (element, data) {
  const FontsFactory = require('../../../factory/fonts');

  if (!FontsFactory.isLoadedFont(data.family)) {
    INKIVE.SVGElementLoading.show(element.parentElement);

    FontsFactory.refreshCustomFonts().then(function () {
      $.fontSpy(data.family, {
        success: () => INKIVE.SVGElementLoading.hide(element.parentElement),
        failure: () => INKIVE.SVGElementLoading.hide(element.parentElement),
        timeOut: 5000
      });
    });
  }
};

TextSetter.prototype.updateBulletPoints = function (element, data) {
  // Update bullet of whole text
  // Possible combinations of ordered and bullet:
  // false false
  // true false
  // false true
  let paragraphIndex = 0;
  // Backward compatibly for App
  if (data.bullet_points === true) {
    data.bullet_points = 1;
    data.bullet_style = constants.text.bulletPoints.defaultStyleId;
    data.bullet_width = 0;
    data.bullet_fill = null;
  } else if (data.bullet_points === false) {
    data.bullet_points = 0;
    data.bullet_style = 0;
    data.bullet_width = 0;
    data.bullet_fill = null;
  }

  this.textarea.text.strings.forEach(string => {
    const bulletLevelToSet = data.bullet_points === 0 ? data.bullet_points : Number(string.bulletLevel) || Number(string.orderedLevel) || data.bullet_points;
    if (bulletLevelToSet <= 1 && (string.bulletLevel || string.orderedLevel)) {
      Array.isArray(this.textarea.text.first_line_margin) && (this.textarea.text.first_line_margin[paragraphIndex] = 0);
      Array.isArray(this.textarea.text.line_margin) && (this.textarea.text.line_margin[paragraphIndex] = 0);
    }

    string.setValues({
      bulletLevel: bulletLevelToSet,
      bulletStyle: data.bullet_style,
      bulletWidth: data.bullet_width,
      bulletFill: data.bullet_fill
    });

    string.orderedLevel && string.bulletLevel && string.setValues({ orderedLevel: 0, orderedStyle: 0, orderedStart: null, orderedNumberingStyle: 0, orderedRegex: '' });

    if (string.isEol) {
      paragraphIndex += 1;
    }
  });

  this.trigger('bullet_points', element.getAttribute('id'), data.bullet_points);
};

TextSetter.prototype.updateOrderedList = function (element, data) {
  // Update ordered list of whole text
  // Possible combinations of ordered and bullet:
  // false false
  // true false
  // false true
  let paragraphIndex = 0;
  this.textarea.text.setValues({
    orderedStart: data.ordered_start
  });

  this.textarea.text.strings.forEach(string => {
    const orderedLevelToSet = data.ordered_list === 0 ? data.ordered_list : Number(string.orderedLevel) || Number(string.bulletLevel) || data.ordered_list;
    if (orderedLevelToSet <= 1 && (string.bulletLevel || string.orderedLevel)) {
      Array.isArray(this.textarea.text.first_line_margin) && (this.textarea.text.first_line_margin[paragraphIndex] = 0);
      Array.isArray(this.textarea.text.line_margin) && (this.textarea.text.line_margin[paragraphIndex] = 0);
    }

    string.setValues({
      orderedLevel: orderedLevelToSet,
      orderedStyle: data.ordered_style,
      orderedWidth: data.ordered_width,
      orderedFill: data.ordered_fill,
      orderedStart: data.ordered_start,
      orderedNumberingStyle: data.ordered_numbering_style,
      orderedRegex: data.ordered_regex
    });

    string.orderedLevel && string.bulletLevel && string.setValues({ bulletLevel: 0, bulletStyle: 0 });
    if (string.isEol) {
      paragraphIndex += 1;
    }
  });

  this.textarea.text.setValues({ ordered: !!data.ordered_list });
  this.trigger('ordered_list', element.getAttribute('id'), data.ordered_list);
};

TextSetter.prototype.updateAutoAdjustable = function (element, data) {
  this.textarea.text.setValues({
    auto_adjustable: data.auto_adjustable,
    original_font_size: data.auto_adjustable
      ? this.textarea.text.font_size
      : null,
    vertical_offset: data.auto_adjustable
      ? null
      : this.textarea.text.auto_adjustable
  });

  this.trigger('auto_adjustable', element.getAttribute('id'), data.auto_adjustable);
};

TextSetter.prototype.updateTextOverflow = function (element, data) {
  data.content = data.text_overflow;
  this.updateTextOverflowContent(element, data);
};

TextSetter.prototype.updateTextOverflowContent = function (element, data) {
  let firstDiffLinePosition = null;

  const newContent = data.content;
  const oldContent = this.textarea.text.textContent;

  const stringDifference = INKIVE.Tool.calculateStringContentDifference(oldContent, newContent);

  const newLetters = stringDifference.added - stringDifference.removed;
  const forceRedraw = data.force_redraw || newLetters <= 0 || newContent.length < 300;
  const cursorPosition = data.cursor_position;
  const newContentStrings = newContent.split('\n');

  // Identify the index of line that the cursor is positioned whenever the content is updated
  const numOfLines = element.querySelectorAll('text').length;
  const lineArray = INKIVE.Helpers.getLineArray(element, numOfLines);

  // cursorPosition is the position after content updated
  // cursorPosition - 1 is the position before content updated
  const positionOutput = INKIVE.Helpers.getCursorLineIndex(lineArray, cursorPosition - newLetters);
  const lineIndex = positionOutput[0];

  forceRedraw || _.any(this.textarea.text.strings, _.bind(function (string, index) {
    const textString = string.isBlank ? '' : string.textContent;
    const lastPosition = this.textarea.text.strings.length - 1;
    const isDiff = string.isEol
      ? newContentStrings[index] !== textString
      : newContentStrings[index].indexOf(textString) === -1;

    if (isDiff || index === lastPosition || index === newContentStrings.length - 1) {
      firstDiffLinePosition = index;
      return true;
    }
  }, this));

  this.textarea.text.setValues({
    firstDiffLinePosition,
    textContent: newContent.replace(/ /g, String.fromCharCode(160)),
    oldContent,
    newLetters,
    lineIndex,
    cursorPosition: cursorPosition || (cursorPosition === 0 ? cursorPosition : null)
  });

  this.trigger('overflow_content', element.getAttribute('id'), newContent);
};

TextSetter.prototype.updateTextContent = function (element, data) {
  data.content = data.text_content;
  if (INKIVE.RestrictionsFacade.isRestrictedDesign()) {
    const updateRestrictionContent = TextRestrictions.updateContent.bind(this);
    updateRestrictionContent(element, data);
  } else {
    this.updateContent(element, data);
  }
};

TextSetter.prototype.updateContent = function (element, data) {
  let firstDiffLinePosition = null;

  const newContent = data.content;
  const oldContent = this.textarea.text.textContent;

  const stringDifference = INKIVE.Tool.calculateStringContentDifference(oldContent, newContent);

  const newLetters = stringDifference.added - stringDifference.removed;
  const forceRedraw = data.force_redraw || newLetters <= 0 || newContent.length < 300;
  const cursorPosition = data.cursor_position;
  const newContentStrings = newContent.split('\n');
  const typing = !!data.typing;

  // Identify the index of line that the cursor is positioned whenever the content is updated
  const numOfLines = element.querySelectorAll('text').length;
  const lineArray = INKIVE.Helpers.getLineArray(element, numOfLines);

  // cursorPosition is the position after content updated
  // cursorPosition - 1 is the position before content updated
  const positionOutput = INKIVE.Helpers.getCursorLineIndex(lineArray, cursorPosition - newLetters);
  const lineIndex = positionOutput[0];

  forceRedraw || _.any(this.textarea.text.strings, _.bind(function (string, index) {
    const textString = string.isBlank ? '' : string.textContent;
    const lastPosition = this.textarea.text.strings.length - 1;
    const isDiff = string.isEol
      ? newContentStrings[index] !== textString
      : newContentStrings[index].indexOf(textString) === -1;

    if (isDiff || index === lastPosition || index === newContentStrings.length - 1) {
      firstDiffLinePosition = index;
      return true;
    }
  }, this));

  this.textarea.setValues({ typing });

  if (data.remove_partial_styles) {
    this.textarea.text.setValues({ styles: [] });
  }

  this.textarea.text.setValues({
    firstDiffLinePosition,
    textContent: newContent.replace(/ /g, String.fromCharCode(160)),
    oldContent,
    newLetters,
    lineIndex,
    direction: /[\u0591-\u07FF]/.test(newContent) ? 'rtl' : null, // regex detects RTL language in content
    cursorPosition: cursorPosition || (cursorPosition === 0 ? cursorPosition : null)
  });

  this.trigger('content', element.getAttribute('id'), newContent);
};

TextSetter.prototype.updateDecoration = function (element, data) {
  const spacing = data.spacing;
  const isUnsettingLetterSpacing = spacing && typeof spacing.letter !== 'undefined' && !spacing.letter;
  const isUnsettingWordSpacing = spacing && typeof spacing.word !== 'undefined' && !spacing.word;
  const hasLockingSpacing =
    (this.textarea.text.letter_spacing && !isUnsettingLetterSpacing) ||
    (this.textarea.text.word_spacing && !isUnsettingWordSpacing);

  if (hasLockingSpacing && data.decoration === 'underline') {
    require('../../../utils/advice').show('spacing-and-underline');
  } else if (data.decoration) {
    this.textarea.text.setPartialStyling(0, 0, 'text-decoration', data.decoration);
    this.trigger('decoration', element.getAttribute('id'), data.decoration);
  } else {
    this.textarea.text.setValues({ text_decoration: data.decoration });
    this.textarea.text.removeSpecificPartialStyling(0, this.textarea.text?.textContent?.length || 0, 'text-decoration');
    this.trigger('decoration', element.getAttribute('id'), data.decoration);
  }
};

TextSetter.prototype.updateAnchor = function (element, data) {
  if (data.anchor === 'justify') data.anchor = 'justify_start';

  this.textarea.text.removeLineStyling();
  this.textarea.text.setValues({ text_anchor: data.anchor });
  if (this.textarea.text.letter_spacing.length) {
    this.textarea.text.setValues({ letter_spacing: 0 });
  }
  this.trigger('anchor', element.getAttribute('id'), data.anchor);
};

TextSetter.prototype.updateColor = function (element, data) {
  this.textarea.text.setValues({ fill: data.color });
  const textLength = this.textarea.text.textContent.length;

  this.textarea.text.removeIncludedStyles('fill', 0, textLength);

  this.trigger('color', element.getAttribute('id'), data.color);
};

TextSetter.prototype.updateColumns = function (element, data) {
  this.textarea.text.setValues({ columns: data.columns });
  this.trigger('columns.number', element.getAttribute('id'), data.columns);
};

TextSetter.prototype.updateColumnsGap = function (element, data) {
  this.textarea.text.setValues({ columns_gap: data.columns_gap });
  this.trigger('columns.gap', element.getAttribute('id'), data.columns);
};

TextSetter.prototype.updateColumnsBalance = function (element, data) {
  this.textarea.text.setValues({ columns_balance: data.columns_balance });
  this.trigger('columns.mode', element.getAttribute('id'), data.columns);
};

TextSetter.prototype.updateHeading = function (element, data) {
  const canvas = this.canvas;
  const content = this.textarea.text.textContent;
  this.textarea.text.checkGlobalPartialStyles();
  this.textarea.setValues({
    heading: Number(data.heading) ? Number(data.heading) : null
  });

  this.textarea.text.setLineStyling(0, content.length, 'heading', 0);

  this.textarea.text.setMultiplePartialStyling(0, content.length, removeFalsyAttributes({
    'font-weight': calculateFontWeight(data.heading),
    'font-size': calculateFontSize(data.heading, canvas)
  }));

  this.textarea.text.setValues({
    font_size: calculateFontSize(data.heading, canvas),
    font_weight: calculateFontWeight(data.heading)
  });

  this.trigger('heading', element.getAttribute('id'), data.heading);

  function calculateFontSize (heading, canvas) {
    const baseSize = Math.min(canvas.contentW, canvas.contentH);
    const fontSize = Math.round(baseSize * constants.tableOfContents.defaultHeadingStyle[heading].size);

    return fontSize;
  }

  function calculateFontWeight (heading) {
    return constants.tableOfContents.defaultHeadingStyle[heading].fontWeight;
  }
};

TextSetter.prototype = RestrictionsDecorate(require('./restrictions/text'), TextSetter.prototype);
